import { captureException } from '@sentry/capacitor';

export const useCalendarFiltersStore = defineStore('calendarFilter', () => {
  const calendarAPI = useCalendarAPI();
  const fitterAPI = useFitterAPI();
  const { t } = useEkI18n();
  const { openToast } = useUserMessages();
  const settingsStore = useSettingsStore();
  const userConfigurationStore = useUserConfigurationStore();

  const filter = ref<CalendarFilter>(createEmptyCalendarFilter());

  const fitterOptions = ref<FitterResponseDTO[]>([]);
  const resourceGroupOptions = ref<ResourceGroupDTO[]>([]);
  const worksheetOptions = ref<WorksheetListResponseDTO[]>([]);
  const categoryOptions = ref<CalendarEventCategoryDTO[]>([]);

  function $reset() {
    fitterOptions.value = [];
    resourceGroupOptions.value = [];
    worksheetOptions.value = [];
    categoryOptions.value = [];
  }

  function createEmptyCalendarFilter() {
    const emptyFilter: CalendarFilter = {
      FitterIDs: [userConfigurationStore.fitterId],
      WorksheetIDs: [],
      CategoryIDs: [],
      ResourceGroupIDs: [],
      ShowActiveProjectsOnly: false,
      ShowSharedAppointments: false
    };

    return emptyFilter;
  }

  function resetFilter() {
    filter.value = createEmptyCalendarFilter();
    saveFilter();
  }

  async function saveFilter() {
    try {
      const shouldSaveFilter = settingsStore.settings.calendarFilterSave;

      const filterToSave = shouldSaveFilter ? filter.value : null;

      await settingsStore.set('calendarFilter', filterToSave);
    } catch (error) {
      captureException(error);
      openToast(t('error.generic'), ToastType.ERROR);
    }
  }

  function createGetEventsPayload(fromDate: string, toDate: string) {
    const payload: GetCalendarEventsRequestDTO = {
      ...filter.value,
      To: toDate,
      From: fromDate
    };

    if (payload.FitterIDs.length === 0) {
      payload.FitterIDs = [userConfigurationStore.fitterId];
    }

    return payload;
  }

  async function getFitters() {
    if (fitterOptions.value.length > 0) {
      return;
    }

    const response = await fitterAPI.getFitters();

    if (response?.HasErrors) {
      openToast(response.ErrorMessage, ToastType.ERROR);

      return;
    }

    if (response?.Data) {
      fitterOptions.value = response.Data;
    }
  }

  async function getCategories() {
    if (categoryOptions.value.length > 0) {
      return;
    }

    const response = await calendarAPI.getEventCategories();

    if (response?.HasErrors) {
      openToast(response.ErrorMessage, ToastType.ERROR);

      return;
    }

    if (response?.Data) {
      categoryOptions.value = response.Data;
    }
  }

  async function getResourceGroups() {
    if (resourceGroupOptions.value.length > 0) {
      return;
    }

    const response = await calendarAPI.getResourceGroups();

    if (response?.HasErrors) {
      openToast(response.ErrorMessage, ToastType.ERROR);

      return;
    }

    if (response?.Data) {
      resourceGroupOptions.value = response.Data;
    }
  }

  const usedFiltersCount = computed(() => {
    const comparedFilters = differencesInObject(filter.value, createEmptyCalendarFilter());

    return Object.values(comparedFilters).filter((value) => value === false).length;
  });

  return {
    filter,
    usedFiltersCount,
    fitterOptions,
    resourceGroupOptions,
    worksheetOptions,
    categoryOptions,
    createGetEventsPayload,
    resetFilter,
    createEmptyCalendarFilter,
    saveFilter,
    getFitters,
    getCategories,
    getResourceGroups,
    $reset
  };
});
